/**
 * #2807 — the Pflanzdichte of a Schlag's current Wirtschaftsjahr.
 *
 * ONE rule carries this whole module: **a density is shown with its unit, or it is
 * shown as unknown.** Never a bare number. A rate the reader cannot name is not a
 * smaller truth than a named one, it is a different number — the #2844 precedent is
 * exact here, where routing a `kg` basis through the normalised quantity silently
 * relabels 5,00 €/dt as 0,05 €/dt. Berries read Pfl/m² and arable reads kg/ha, and
 * those differ by four orders of magnitude, so an unlabelled figure is worse than
 * no figure at all.
 *
 * The unit is therefore NOT derived here from the attribute key. The producer
 * (`worker/src/services/agronomy/planting_density.py::density_display_unit`) resolves
 * it from the crop_cycle's own quantity_kind/unit/basis and stamps it alongside the
 * value; `stamp_current_crop` writes the pair coherently or writes neither. This
 * module only refuses to render half a pair — the belt to that producer's braces,
 * because a row written before #2807, or by an operator upsert, can still carry a
 * value with no unit.
 */

export const DENSITY_ATTRIBUTE = 'current_planting_density';
export const DENSITY_UNIT_ATTRIBUTE = 'current_planting_density_unit';
export const DENSITY_ESTIMATED_ATTRIBUTE = 'current_planting_density_estimated';

/** The sibling keys the density cell renders itself — hidden as rows of their own. */
export const DENSITY_COMPANION_ATTRIBUTES = [DENSITY_UNIT_ATTRIBUTE, DENSITY_ESTIMATED_ATTRIBUTE];

export interface PlantingDensity {
    /** The rate, de-DE formatted, WITHOUT its unit. */
    value: number;
    /** The rate unit ("Pfl/m²", "kg/ha"), or null when the twin could not name one. */
    unit: string | null;
    /** True when the rate was DERIVED (count ÷ area) rather than recorded. */
    estimated: boolean;
}

function toNumber(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    // A re-read twin row can carry a float64 back as a numeric string.
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
    return null;
}

/**
 * True when the twin says this density was derived rather than measured.
 *
 * Truthy-coerced on purpose: the attribute is a real boolean on the write path, but
 * an as-of reconstruction or an operator patch can round-trip it as `"true"` / `1`.
 * The safe direction is unambiguous — reading an estimate as measured is the error
 * that matters, so anything that is not falsy marks the value as an estimate.
 */
export function isEstimated(raw: unknown): boolean {
    if (typeof raw === 'string') return raw.trim() !== '' && raw.trim().toLowerCase() !== 'false';
    return Boolean(raw);
}

/**
 * Read the density triple off a field's properties, or `null` when there is no
 * density to show at all.
 *
 * A present value with an ABSENT unit still returns an entry — with `unit: null`,
 * which the cell renders as *unbekannt*. Dropping it here instead would hide the
 * fact that the twin holds an unusable number, and "no density recorded" and "a
 * density nobody can interpret" are different things to tell a farmer.
 */
export function readPlantingDensity(properties: Record<string, unknown> | undefined): PlantingDensity | null {
    if (!properties) return null;
    const value = toNumber(properties[DENSITY_ATTRIBUTE]);
    if (value === null || value <= 0) return null;
    const rawUnit = properties[DENSITY_UNIT_ATTRIBUTE];
    const unit = typeof rawUnit === 'string' && rawUnit.trim() !== '' ? rawUnit.trim() : null;
    return { value, unit, estimated: isEstimated(properties[DENSITY_ESTIMATED_ATTRIBUTE]) };
}

/** The rendered rate, de-DE, e.g. `7,8 Pfl/m²`. Only valid when `unit` is present. */
export function formatDensity(density: PlantingDensity): string {
    const number = density.value.toLocaleString('de-DE', { maximumFractionDigits: 2 });
    return density.unit ? `${number} ${density.unit}` : number;
}
